import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import './Auth.css';
import { trackEvent } from '../../../utils/analytics';
import { storeSignupCheckout } from '../../../utils/signupCheckout';
import {
  formatSubscriptionInterval,
  formatSubscriptionPrice,
} from '../../../constants/subscriptionPlans';

const formatTrialEndDate = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed);
};

// Stripe's iframe only says "Total due today $0.00"; nothing in our chrome
// confirmed that the free trial had taken. This spells out the first-charge
// date and the price the server attached to the Checkout Session, so the
// customer can see the trial is registered before they enter a card.
const TrialBillingNotice = ({ billing, fallbackPlanName }) => {
  const trialEndDate = formatTrialEndDate(billing?.trial?.endsAt);
  if (!trialEndDate) return null;

  const amount = formatSubscriptionPrice(billing?.price);
  const interval = formatSubscriptionInterval(billing?.price);
  const planName = billing?.planName || fallbackPlanName;

  return (
    <p className="checkout-trial-notice">
      You won&apos;t be charged until <strong>{trialEndDate}</strong>.
      {amount && interval && (
        <>
          {' '}After that, your {planName} plan is <strong>{amount}/{interval}</strong>.
        </>
      )}
    </p>
  );
};

const EmbeddedCheckoutCard = ({ clientSecret, publishableKey }) => {
  const stripePromise = useMemo(
    () => (publishableKey ? loadStripe(publishableKey) : null),
    [publishableKey]
  );
  if (!clientSecret || !stripePromise) return null;

  return (
    <div className="embedded-checkout-card">
      <div className="embedded-checkout-card__checkout">
        <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </div>
    </div>
  );
};

const RETRYABLE_ERROR_CODES = new Set([
  'CHECKOUT_SESSION_FAILED',
  'CHECKOUT_SESSION_NOT_RESUMABLE',
  'SUBSCRIPTION_BILLING_NOT_CONFIGURED',
  'SIGNUP_COMPLETION_IN_PROGRESS',
]);
const INVALID_SIGNUP_LINK_CODES = new Set([
  'SIGNUP_LINK_MISSING',
  'SIGNUP_LINK_INVALID',
  'SIGNUP_LINK_NOT_FOUND',
  'INVALID_ACCOUNT_MODE',
  'CHECKOUT_SESSION_INVALID',
]);
const INVALID_SIGNUP_LINK_MESSAGE =
  'This signup link is invalid or incomplete. Request a new link or restart signup.';

const getApiError = (error) => {
  const payload = error?.response?.data || {};
  const nestedError = payload?.error && typeof payload.error === 'object'
    ? payload.error
    : null;
  const code = payload.code || nestedError?.code || error?.code || '';
  const message =
      (typeof payload.error === 'string' ? payload.error : nestedError?.message) ||
      payload.message ||
      error?.message ||
      'Unable to prepare checkout. Please try again.';
  return {
    code,
    message: INVALID_SIGNUP_LINK_CODES.has(code) ? INVALID_SIGNUP_LINK_MESSAGE : message,
  };
};

const SignupCheckout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pendingSignupId = searchParams.get('pendingSignupId');
  const checkoutToken = searchParams.get('token');

  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [publishableKey, setPublishableKey] = useState('');
  const [activationMessage, setActivationMessage] = useState('');
  const [accountMode, setAccountMode] = useState('');
  const [billing, setBilling] = useState(null);
  const [errorCode, setErrorCode] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState('idle');
  const [resendMessage, setResendMessage] = useState('');

  useEffect(() => {
    if (!pendingSignupId || !checkoutToken) {
      setError(INVALID_SIGNUP_LINK_MESSAGE);
      setErrorCode('SIGNUP_LINK_MISSING');
      setStatus('error');
      return;
    }

    let isMounted = true;

    const startCheckout = async () => {
      setStatus('loading');
      setError('');
      setErrorCode('');
      setClientSecret('');
      setPublishableKey('');
      setBilling(null);
      try {
        const verificationResponse = await axios.post('/api/auth/signup/verify-link', {
          pendingSignupId,
          checkoutToken,
        });

        if (!isMounted) return;

        const { name, email, accountMode: verifiedAccountMode } = verificationResponse.data || {};
        const modeLabel = verifiedAccountMode === 'team' ? 'Team Mode' : 'Single Mode';
        setAccountMode(verifiedAccountMode || '');
        if (email) setResendEmail(email);
        setActivationMessage(
          name
            ? `Hi ${name}! Enter your payment details below to activate your ${modeLabel} plan.`
            : `Enter your payment details below to activate your ${modeLabel} plan.`
        );

        const successUrl = `${window.location.origin}/signup/success`;
        const cancelUrl = `${window.location.origin}/signup/cancel`;

        const sessionResponse = await axios.post(
          '/api/payment/create-checkout-session',
          {
            pendingSignupId,
            checkoutToken,
            successUrl,
            cancelUrl,
          }
        );

        if (!isMounted) return;

        const {
          embeddedClientSecret,
          publishableKey: selectedPublishableKey,
          sessionId,
          planName,
          trial,
          price,
        } = sessionResponse.data || {};
        if (!embeddedClientSecret || !selectedPublishableKey) {
          const invalidCheckoutResponse = new Error(INVALID_SIGNUP_LINK_MESSAGE);
          invalidCheckoutResponse.code = 'CHECKOUT_SESSION_INVALID';
          throw invalidCheckoutResponse;
        }

        // For /signup/success, where Stripe returns this tab after payment, in
        // case it has to be signed back in there.
        storeSignupCheckout({ sessionId, pendingSignupId, checkoutToken });
        setClientSecret(embeddedClientSecret);
        setPublishableKey(selectedPublishableKey);
        setBilling({ planName, trial, price });
        setStatus('ready');
        trackEvent('signup_promo_conversion', {
          status: 'checkout_started',
          source: 'email_link',
          pendingSignupId,
        });
      } catch (err) {
        if (!isMounted) return;
        const apiError = getApiError(err);
        setError(apiError.message);
        setErrorCode(apiError.code);
        setStatus('error');
      }
    };

    startCheckout();

    return () => {
      isMounted = false;
    };
  }, [attempt, checkoutToken, pendingSignupId]);

  const resendSignupLink = async (event) => {
    event.preventDefault();
    const normalizedEmail = resendEmail.trim();
    if (!normalizedEmail) {
      setResendStatus('error');
      setResendMessage('Enter the email address used for signup.');
      return;
    }

    setResendStatus('sending');
    setResendMessage('');
    try {
      const response = await axios.post('/api/auth/signup/resend-link', {
        email: normalizedEmail,
      });
      setResendStatus('success');
      setResendMessage(response.data?.message || 'A new signup link has been sent.');
    } catch (resendError) {
      const apiError = getApiError(resendError);
      setResendStatus('error');
      setResendMessage(apiError.message);
    }
  };

  if (status === 'loading') {
    return (
      <div className="auth-page">
        <span className="deco deco--1 auth-deco--tl" aria-hidden="true" />
        <span className="deco deco--2 auth-deco--br" aria-hidden="true" />
        <div className="auth-inner">
          <section className="auth-card">
            <h1>Validating your signup link…</h1>
            <p>Hang tight while we confirm your details and prepare checkout.</p>
          </section>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    const alreadyCompleted = errorCode === 'SIGNUP_ALREADY_COMPLETED';
    const canRetry = !errorCode || RETRYABLE_ERROR_CODES.has(errorCode);
    return (
      <div className="auth-page">
        <span className="deco deco--1 auth-deco--tl" aria-hidden="true" />
        <span className="deco deco--2 auth-deco--br" aria-hidden="true" />
        <div className="auth-inner">
          <section className="auth-card">
            <h1>We couldn't open checkout</h1>
            {accountMode && <p className="eyebrow">{accountMode === 'team' ? 'Team Mode' : 'Single Mode'}</p>}
            <p role="alert">{error || 'This signup link is no longer valid.'}</p>

            {canRetry && (
              <button className="auth-submit" type="button" onClick={() => setAttempt((value) => value + 1)}>
                Try again
              </button>
            )}

            {!alreadyCompleted && (
              <form className="signup-link-recovery" onSubmit={resendSignupLink}>
                <label htmlFor="signup-recovery-email">Email address</label>
                <input
                  id="signup-recovery-email"
                  type="email"
                  value={resendEmail}
                  onChange={(event) => setResendEmail(event.target.value)}
                  autoComplete="email"
                  required
                />
                <button className="auth-submit signup-link-recovery__button" type="submit" disabled={resendStatus === 'sending'}>
                  {resendStatus === 'sending' ? 'Sending…' : 'Send a new link'}
                </button>
                {resendMessage && (
                  <p className={`helper-text ${resendStatus === 'error' ? 'error' : 'success'}`} role="status">
                    {resendMessage}
                  </p>
                )}
              </form>
            )}

            <div className="signup-link-recovery__actions">
              {alreadyCompleted && (
                <button className="auth-submit" type="button" onClick={() => navigate('/login')}>
                  Sign in
                </button>
              )}
              <button className="auth-submit auth-submit--secondary" type="button" onClick={() => navigate('/signup')}>
                Restart signup
              </button>
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page auth-page--checkout">
      <span className="deco deco--1 auth-deco--tl" aria-hidden="true" />
      <span className="deco deco--2 auth-deco--br" aria-hidden="true" />
      <div className="auth-inner auth-inner--checkout">
        <header className="auth-header">
          <h1>Complete your setup</h1>
          {activationMessage && <p className="sub">{activationMessage}</p>}
        </header>

        <TrialBillingNotice
          billing={billing}
          fallbackPlanName={accountMode === 'team' ? 'Team Mode' : 'Single Mode'}
        />

        <EmbeddedCheckoutCard clientSecret={clientSecret} publishableKey={publishableKey} />
      </div>
    </div>
  );
};

export default SignupCheckout;
